// src/context/persona/assist/ProposalsStep.jsx — "Proposte", step 2 of "Suggerisci personas" (v198; the second
// half of the old ProposePanel). Each proposal: name · market · action · the AI's rationale (plain text), and its
// button: new → "Crea con l'AI" (the AI-create process, pre-filled, in the same modal), personalize → "Adatta con
// l'AI", enable → "Attiva" (turns the persona on; `onEnable(id)` resolves true | false and the OUTCOME is shown on
// the row — the page's own line is behind the overlay), keep → none. Both AI actions are disabled while a job
// runs (a second start would only answer 409 PERSONA_JOB_ACTIVE). Footer: Indietro → "Descrivi" (request kept).
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalFooter } from '../../../components/ui/WizardModal.jsx';
import { COLORS, SEMANTIC } from '../../../constants/theme.js';
import { choiceHint, footerRow, ghostBtn, primaryBtn } from '../personaStyles.js';
import DocResults from './DocResults.jsx';

const listStyle = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '10px' };
const itemStyle = {
  padding: '12px 14px', border: `1px solid ${COLORS.border}`, background: COLORS.surface,
  display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap',
};
const metaStyle = { fontSize: '12px', color: COLORS.textSecondary, marginTop: '2px' };

function EnableAction({ t, id, onEnable }) {
  const [state, setState] = useState(null);   // null | 'busy' | 'done' | 'failed'
  if (state === 'done') {
    return <span role="status" style={{ fontSize: '13px', color: SEMANTIC.verde.fg }}>{t('personas.assist.enabled')}</span>;
  }
  const busy = state === 'busy';
  async function enable() {
    setState('busy');
    let ok = false;
    try {
      ok = Boolean(await onEnable(id));
    } catch {
      ok = false;
    }
    setState(ok ? 'done' : 'failed');
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
      <button type="button" disabled={busy} style={ghostBtn(busy)} onClick={enable}>
        {t('personas.assist.doEnable')}
      </button>
      {state === 'failed' && (
        <span role="alert" style={{ fontSize: '12px', color: SEMANTIC.errore.fg }}>{t('personas.assist.enableFailed')}</span>
      )}
    </div>
  );
}

function ProposalRow({ t, p, jobBusy, onCreateFrom, onPersonalize, onEnable }) {
  let action = null;
  if (p.action === 'new') {
    action = (
      <button
        type="button" disabled={jobBusy} style={primaryBtn(jobBusy)}
        onClick={() => onCreateFrom({ name: p.name, market: p.market })}
      >
        {t('personas.assist.doCreate')}
      </button>
    );
  } else if (p.action === 'personalize' && p.existing_id) {
    action = (
      <button type="button" disabled={jobBusy} style={ghostBtn(jobBusy)} onClick={() => onPersonalize(p.existing_id)}>
        {t('personas.assist.doPersonalize')}
      </button>
    );
  } else if (p.action === 'enable' && p.existing_id) {
    action = <EnableAction t={t} id={p.existing_id} onEnable={onEnable} />;
  }
  return (
    <li style={itemStyle} data-testid="persona-proposal">
      <div style={{ flex: 1, minWidth: '220px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: COLORS.ink }}>{p.name}</div>
        <div style={metaStyle}>{t(`personas.markets.${p.market}`)} · {t(`personas.assist.action.${p.action}`)}</div>
        {p.rationale && <p style={{ ...choiceHint, marginTop: '6px' }}>{p.rationale}</p>}
      </div>
      {action}
    </li>
  );
}

// "18:42" — or "3 ott, 18:42" when the list is not from today. null when the result carries no time.
function formatGeneratedAt(at, language) {
  if (typeof at !== 'number') return null;
  const when = new Date(at);
  const time = when.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
  if (when.toDateString() === new Date().toDateString()) return time;
  return `${when.toLocaleDateString(language, { day: 'numeric', month: 'short' })}, ${time}`;
}

export default function ProposalsStep({ result, jobBusy = false, onCreateFrom, onPersonalize, onEnable, onBack }) {
  const { t, i18n } = useTranslation('context');
  const proposals = Array.isArray(result?.proposals) ? result.proposals : [];
  const generatedAt = formatGeneratedAt(result?.at, i18n?.language);
  return (
    <div data-testid="persona-proposals-step">
      {generatedAt && (
        <p style={{ ...choiceHint, marginTop: 0, marginBottom: '12px' }}>{t('personas.assist.proposalsGeneratedAt', { time: generatedAt })}</p>
      )}
      <ul style={listStyle}>
        {proposals.map((p, i) => (
          <ProposalRow
            key={`${p.existing_id || p.name}-${i}`} t={t} p={p} jobBusy={jobBusy}
            onCreateFrom={onCreateFrom} onPersonalize={onPersonalize} onEnable={onEnable}
          />
        ))}
      </ul>
      {result?.kb_used === false && <p style={{ ...choiceHint, marginTop: '10px' }}>{t('personas.assist.kbUnused')}</p>}
      <DocResults documents={result?.documents} />
      <ModalFooter>
        <div style={footerRow}>
          <button type="button" onClick={onBack} style={ghostBtn(false)}>{t('personas.wizard.back')}</button>
        </div>
      </ModalFooter>
    </div>
  );
}
